'use client';

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Hub } from 'aws-amplify/utils';
import { getNestAwsSession, invalidateSessionCache, LoginInfo } from '@/app/components/header/nestingale_auth';

export interface SessionInfo {
    session: LoginInfo | null;
    providerType: string;
    isSessionValid: boolean;
    userType?: string;
}

interface UserSessionContextProps {
    sessionInfo: SessionInfo | null;
    setSessionInfo: React.Dispatch<React.SetStateAction<SessionInfo | null>>;
    /** true while the initial (or any manual) session fetch is in progress */
    isLoadingSession: boolean;
    /** @deprecated use isLoadingSession */
    isStartedFetchingSessionInfo: boolean;
    setIsStartedFetchingSessionInfo: React.Dispatch<React.SetStateAction<boolean>>;
    /** Manually re-fetch the session (e.g. after login) */
    fetchSession: () => Promise<void>;
}

const UserSessionContext = createContext<UserSessionContextProps | undefined>(undefined);

export const UserSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
    const [isLoadingSession, setIsLoadingSession] = useState(true);

    // Track whether we've already kicked off the initial fetch to avoid double-firing in Strict Mode
    const didInitRef = useRef(false);

    const fetchSession = useCallback(async () => {
        setIsLoadingSession(true);
        try {
            const session = await getNestAwsSession();
            setSessionInfo(session);
        } catch {
            // Unexpected error — treat as no session
            setSessionInfo({ session: null, providerType: '', isSessionValid: false });
        } finally {
            setIsLoadingSession(false);
        }
    }, []);

    // Self-initialise on mount
    useEffect(() => {
        if (didInitRef.current) return;
        didInitRef.current = true;
        fetchSession();
    }, [fetchSession]);

    // Keep session fresh: re-fetch when Amplify fires auth events
    useEffect(() => {
        const unsubscribe = Hub.listen('auth', ({ payload }) => {
            switch (payload.event) {
                case 'signedIn':
                case 'signedOut':
                case 'tokenRefresh':
                    // Cache has already been busted by the Hub listener in nestingale_auth.tsx
                    fetchSession();
                    break;
            }
        });
        return unsubscribe;
    }, [fetchSession]);

    return (
        <UserSessionContext.Provider
            value={{
                sessionInfo,
                setSessionInfo,
                isLoadingSession,
                // Backward-compat alias
                isStartedFetchingSessionInfo: isLoadingSession,
                setIsStartedFetchingSessionInfo: setIsLoadingSession,
                fetchSession,
            }}
        >
            {children}
        </UserSessionContext.Provider>
    );
};

export const useUserSession = (): UserSessionContextProps => {
    const context = useContext(UserSessionContext);
    if (context === undefined) {
        throw new Error('useUserSession must be used within a UserSessionProvider');
    }
    return context;
};
